import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const sections = [
  {
    n: "01",
    title: "Overview and Values",
    body: (
      <>
        <p>
          At Sebastian Visual Corp., my reputation rides on the quality of my work and my ethical standards. This document outlines how your personal information is protected, as well as the terms, copyright ownership, and usage rights regarding the media I create for you.
        </p>
        <p>
          By hiring Sebastian Visual Corp. or using this website, you agree to the terms outlined below.
        </p>
      </>
    ),
  },
  {
    n: "02",
    title: "Privacy Policy & Data Protection",
    body: (
      <dl className="space-y-6">
        <div>
          <dt className="h-eyebrow mb-2">Information Collection</dt>
          <dd>When you inquire about or book a service, I collect necessary information such as your name, email address, phone number, and event details.</dd>
        </div>
        <div>
          <dt className="h-eyebrow mb-2">Use of Information</dt>
          <dd>Your information is used strictly to communicate with you, provide quotes, manage your booking, and deliver your final products.</dd>
        </div>
        <div>
          <dt className="h-eyebrow mb-2">Data Security</dt>
          <dd>I will never sell, trade, or rent your personal information to third parties. Your privacy and security are paramount.</dd>
        </div>
      </dl>
    ),
  },
  {
    n: "03",
    title: "Copyright & Ownership Rights",
    body: (
      <dl className="space-y-6">
        <div>
          <dt className="h-eyebrow mb-2">Full Copyright Retention</dt>
          <dd>Sebastian Visual Corp. retains full, legal, and complete copyright ownership of all media (including but not limited to photographs, video footage, and audio) captured during your session or event.</dd>
        </div>
        <div>
          <dt className="h-eyebrow mb-2">Provided Copies</dt>
          <dd>As a client, you are purchasing the service of my photography/videography and a copy of the finalized, edited images and videos. You are not purchasing the copyright to the media itself.</dd>
        </div>
      </dl>
    ),
  },
  {
    n: "04",
    title: "Client Usage Rights (Licensing)",
    body: (
      <dl className="space-y-6">
        <div>
          <dt className="h-eyebrow mb-2">Personal Use</dt>
          <dd>You are granted a perpetual, non-exclusive license to use the delivered edits for personal purposes. You are highly encouraged to share your photos and videos with friends, family, and on your personal social media accounts.</dd>
        </div>
        <div>
          <dt className="h-eyebrow mb-2">Commercial &amp; Promotional Ban</dt>
          <dd>The media provided to you cannot be used to sell a product, advertise a business, or printed for promotional use by you or any third-party vendors (such as venues, makeup artists, or event planners).</dd>
        </div>
        <div>
          <dt className="h-eyebrow mb-2">Commercial Licensing</dt>
          <dd>If you or a third party wish to use the media for commercial or advertising purposes, you must contact Sebastian Visual Corp. directly. Commercial usage requires a separate, signed consent agreement and is subject to a commission or licensing fee.</dd>
        </div>
      </dl>
    ),
  },
  {
    n: "05",
    title: "RAW Files & Unedited Media",
    body: (
      <p>
        Standard packages include fully edited, high-resolution final deliverables. Unedited, RAW photo and video files are not included as part of standard service packages. Because RAW files are the unfinished foundation of my work, they are tightly protected but may be purchased at an additional, agreed-upon cost.
      </p>
    ),
  },
  {
    n: "06",
    title: "Studio Usage & Ethical Guarantee",
    body: (
      <dl className="space-y-6">
        <div>
          <dt className="h-eyebrow mb-2">Advertising</dt>
          <dd>Sebastian Visual Corp. reserves the right to use the captured media for internet advertising, portfolio displays, social media marketing, and promotional materials to showcase the quality of my work.</dd>
        </div>
        <div>
          <dt className="h-eyebrow mb-2">The Ethical Guarantee</dt>
          <dd>I respect my clients deeply. I guarantee that your images and videos will never be used for inappropriate purposes or with malicious intent. I build my business on trust and will always represent you in a positive, professional light.</dd>
        </div>
      </dl>
    ),
  },
  {
    n: "07",
    title: "Limitation of Liability & Client Satisfaction Guarantee",
    body: (
      <p>
        While every precaution is taken to ensure flawless capture and delivery, Sebastian Visual Corp. cannot be held liable for missed moments due to factors outside of my control (e.g., extreme weather, guest interference, or equipment malfunction). In the highly unlikely event of a data failure or a missed moment, my absolute priority is your complete satisfaction. In lieu of a cash refund, a credit will be applied to your account, or a makeup session will be provided. To guarantee you receive the value you expect, this makeup session can either be a reshoot of the specific missed moment or a completely new shoot equal to the value of the missed time.
      </p>
    ),
  },
  {
    n: "08",
    title: "Governing Law",
    body: (
      <p>
        These terms are governed by and construed in accordance with the laws of the Province of British Columbia, Canada.
      </p>
    ),
  },
];

export default function Legal() {
  return (
    <main className="pt-24 md:pt-28" data-testid="page-legal">
      <section className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="h-eyebrow mb-6">§ Legal</div>
        <h1 className="h-display text-5xl md:text-7xl leading-[0.95] max-w-4xl">
          Privacy Policy<br/>
          <span className="italic font-serif-editorial text-[var(--gold)] font-light">&amp; Terms of Service.</span>
        </h1>
        <p className="font-serif-editorial italic text-lg md:text-xl text-[var(--ink)]/80 mt-6 max-w-2xl">
          Sebastian Visual Corp.
        </p>
      </section>

      {/* Table of contents */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 mt-16 md:mt-20">
        <div className="border-t border-white/5 pt-10">
          <div className="h-eyebrow mb-6">§ Contents</div>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3" data-testid="legal-toc">
            {sections.map((s) => (
              <li key={s.n}>
                <a
                  href={`#section-${s.n}`}
                  className="group flex items-baseline gap-4 py-2 border-b border-white/5 hover:border-[var(--royal)] transition-colors"
                  data-testid={`legal-toc-${s.n}`}
                >
                  <span className="numeral text-lg w-8">{s.n}</span>
                  <span className="font-sans-display text-base md:text-lg text-[var(--ink)] group-hover:text-[var(--royal)] transition-colors">
                    {s.title}
                  </span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Sections */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 mt-16 md:mt-20 space-y-12 md:space-y-16">
        {sections.map((s, i) => (
          <motion.article
            key={s.n}
            id={`section-${s.n}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: i * 0.03, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 border-t border-white/5 pt-8 md:pt-10 scroll-mt-24"
            data-testid={`legal-section-${s.n}`}
          >
            <div className="md:col-span-3">
              <div className="numeral text-6xl md:text-7xl leading-none">{s.n}</div>
              <div className="h-eyebrow mt-3">§ {s.n}</div>
            </div>
            <div className="md:col-span-9">
              <h2 className="h-display text-2xl md:text-4xl leading-tight mb-6 md:mb-8">
                {s.title}
              </h2>
              <div className="font-serif-editorial text-lg md:text-xl leading-relaxed text-[var(--ink)]/85 space-y-5">
                {s.body}
              </div>
            </div>
          </motion.article>
        ))}
      </section>

      {/* Footer band */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 mt-20 md:mt-24 pt-10 border-t border-white/5">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="h-eyebrow mb-3">§ Questions</div>
            <p className="font-serif-editorial text-lg text-[var(--ink)]/80 max-w-md">
              For clarifications regarding these terms, please contact the studio directly.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="legal-cta-contact"
            className="inline-flex items-center gap-3 bg-[var(--royal)] hover:bg-[var(--royal-soft)] text-white px-8 py-4 font-sans-display uppercase tracking-[0.3em] text-[11px] transition-colors"
          >
            Contact the Studio →
          </Link>
        </div>
        <p className="font-mono-fine text-xs text-[var(--ink-muted)] mt-10">
          Last updated: November 2026 · Sebastian Visual Corp. · Surrey, BC
        </p>
      </section>

      <div className="h-16 md:h-24" />
    </main>
  );
}
